import { NetworkTransport } from "./types.ts";

export type ChainMeta = {
    chainName: string;
    symbol: string;
    gasToken: string;
    transport: NetworkTransport;
    chainId?: number;
    nativeDecimals: number;
    defaultRpcUrls: readonly string[];
    explorerUrl: string;
    minGas?: bigint;
};

const SOLANA_MIN_GAS_TOP_UP_LAMPORTS = 1_500_000n;

export const chains = {
    ARB: {
        chainName: "Arbitrum One",
        symbol: "ARB",
        gasToken: "ETH",
        transport: NetworkTransport.Evm,
        chainId: 42161,
        nativeDecimals: 18,
        defaultRpcUrls: ["https://arb1.arbitrum.io/rpc"],
        explorerUrl: "https://arbiscan.io",
    },
    AVAX: {
        chainName: "Avalanche",
        symbol: "AVAX",
        gasToken: "AVAX",
        transport: NetworkTransport.Evm,
        chainId: 43114,
        nativeDecimals: 18,
        defaultRpcUrls: [
            "https://api.avax.network/ext/bc/C/rpc",
            "https://avalanche-c-chain-rpc.publicnode.com",
        ],
        explorerUrl: "https://snowtrace.io",
    },
    BASE: {
        chainName: "Base",
        symbol: "BASE",
        gasToken: "ETH",
        transport: NetworkTransport.Evm,
        chainId: 8453,
        nativeDecimals: 18,
        defaultRpcUrls: [
            "https://mainnet.base.org",
            "https://base-rpc.publicnode.com",
        ],
        explorerUrl: "https://basescan.org",
    },
    BERA: {
        chainName: "Berachain",
        symbol: "BERA",
        gasToken: "BERA",
        transport: NetworkTransport.Evm,
        chainId: 80094,
        nativeDecimals: 18,
        defaultRpcUrls: ["https://rpc.berachain.com"],
        explorerUrl: "https://berascan.com",
    },
    CFX: {
        chainName: "Conflux eSpace",
        symbol: "CFX",
        gasToken: "CFX",
        transport: NetworkTransport.Evm,
        chainId: 1030,
        nativeDecimals: 18,
        defaultRpcUrls: ["https://evm.confluxrpc.com/"],
        explorerUrl: "https://evm.confluxscan.org",
    },
    CODEX: {
        chainName: "Codex",
        symbol: "CODEX",
        gasToken: "ETH",
        transport: NetworkTransport.Evm,
        chainId: 81224,
        nativeDecimals: 18,
        defaultRpcUrls: ["https://rpc.codex.xyz"],
        explorerUrl: "https://explorer.codex.xyz",
    },
    ETH: {
        chainName: "Ethereum",
        symbol: "ETH",
        gasToken: "ETH",
        transport: NetworkTransport.Evm,
        chainId: 1,
        nativeDecimals: 18,
        defaultRpcUrls: [
            "https://ethereum-rpc.publicnode.com",
            "https://mainnet.gateway.tenderly.co",
            "https://eth.api.pocket.network",
        ],
        explorerUrl: "https://etherscan.io",
    },
    FLR: {
        chainName: "Flare",
        symbol: "FLR",
        gasToken: "SGB",
        transport: NetworkTransport.Evm,
        chainId: 14,
        nativeDecimals: 18,
        defaultRpcUrls: [
            "https://rpc.ankr.com/flare",
            "https://flare-api.flare.network/ext/C/rpc",
        ],
        explorerUrl: "https://flarescan.com",
    },
    HBAR: {
        chainName: "Hedera",
        symbol: "HBAR",
        gasToken: "HBAR",
        transport: NetworkTransport.Evm,
        chainId: 295,
        nativeDecimals: 18,
        defaultRpcUrls: [
            "https://mainnet.hashio.io/api",
            "https://295.rpc.thirdweb.com",
        ],
        explorerUrl: "https://hashscan.io/mainnet",
    },
    HYPE: {
        chainName: "HyperEVM",
        symbol: "HYPE",
        gasToken: "HYPE",
        transport: NetworkTransport.Evm,
        chainId: 999,
        nativeDecimals: 18,
        defaultRpcUrls: ["https://rpc.hyperliquid.xyz/evm"],
        explorerUrl: "https://hyperevmscan.io",
    },
    INK: {
        chainName: "Ink",
        symbol: "INK",
        gasToken: "ETH",
        transport: NetworkTransport.Evm,
        chainId: 57073,
        nativeDecimals: 18,
        defaultRpcUrls: ["https://rpc-gel.inkonchain.com"],
        explorerUrl: "https://explorer.inkonchain.com",
    },
    LINEA: {
        chainName: "Linea",
        symbol: "LINEA",
        gasToken: "ETH",
        transport: NetworkTransport.Evm,
        chainId: 59144,
        nativeDecimals: 18,
        defaultRpcUrls: [
            "https://rpc.linea.build",
            "https://linea-rpc.publicnode.com",
        ],
        explorerUrl: "https://lineascan.build",
    },
    MEGAETH: {
        chainName: "MegaETH",
        symbol: "MEGAETH",
        gasToken: "ETH",
        transport: NetworkTransport.Evm,
        chainId: 4326,
        nativeDecimals: 18,
        defaultRpcUrls: ["https://mainnet.megaeth.com/rpc"],
        explorerUrl: "https://mega.etherscan.io",
    },
    MNT: {
        chainName: "Mantle",
        symbol: "MNT",
        gasToken: "MNT",
        transport: NetworkTransport.Evm,
        chainId: 5000,
        nativeDecimals: 18,
        defaultRpcUrls: ["https://rpc.mantle.xyz"],
        explorerUrl: "https://mantlescan.xyz",
    },
    MON: {
        chainName: "Monad",
        symbol: "MON",
        gasToken: "MON",
        transport: NetworkTransport.Evm,
        chainId: 143,
        nativeDecimals: 18,
        defaultRpcUrls: [
            "https://rpc3.monad.xyz",
            "https://rpc-mainnet.monadinfra.com",
        ],
        explorerUrl: "https://monadexplorer.com",
    },
    MORPH: {
        chainName: "Morph",
        symbol: "MORPH",
        gasToken: "ETH",
        transport: NetworkTransport.Evm,
        chainId: 2818,
        nativeDecimals: 18,
        defaultRpcUrls: ["https://rpc.morph.network"],
        explorerUrl: "https://explorer.morph.network",
    },
    OP: {
        chainName: "Optimism",
        symbol: "OP",
        gasToken: "ETH",
        transport: NetworkTransport.Evm,
        chainId: 10,
        nativeDecimals: 18,
        defaultRpcUrls: [
            "https://mainnet.optimism.io",
            "https://optimism-rpc.publicnode.com",
        ],
        explorerUrl: "https://optimistic.etherscan.io",
    },
    PLASMA: {
        chainName: "Plasma",
        symbol: "PLASMA",
        gasToken: "XPL",
        transport: NetworkTransport.Evm,
        chainId: 9745,
        nativeDecimals: 18,
        defaultRpcUrls: ["https://rpc.plasma.to"],
        explorerUrl: "https://plasmascan.to",
    },
    PLUME: {
        chainName: "Plume",
        symbol: "PLUME",
        gasToken: "PLUME",
        transport: NetworkTransport.Evm,
        chainId: 98866,
        nativeDecimals: 18,
        defaultRpcUrls: ["https://rpc.plume.org"],
        explorerUrl: "https://explorer.plume.org",
    },
    POL: {
        chainName: "Polygon PoS",
        symbol: "POL",
        gasToken: "POL",
        transport: NetworkTransport.Evm,
        chainId: 137,
        nativeDecimals: 18,
        defaultRpcUrls: [
            "https://polygon-bor-rpc.publicnode.com",
            "https://rpc-mainnet.matic.quiknode.pro",
        ],
        explorerUrl: "https://polygonscan.com",
    },
    RBTC: {
        chainName: "Rootstock",
        symbol: "RBTC",
        gasToken: "RBTC",
        transport: NetworkTransport.Evm,
        chainId: 30,
        nativeDecimals: 18,
        defaultRpcUrls: ["https://public-node.rsk.co"],
        explorerUrl: "https://rootstock.blockscout.com",
    },
    SEI: {
        chainName: "Sei",
        symbol: "SEI",
        gasToken: "SEI",
        transport: NetworkTransport.Evm,
        chainId: 1329,
        nativeDecimals: 18,
        defaultRpcUrls: [
            "https://sei.api.pocket.network",
            "https://evm-rpc.sei-apis.com",
        ],
        explorerUrl: "https://seitrace.com",
    },
    SOL: {
        chainName: "Solana",
        symbol: "SOL",
        gasToken: "SOL",
        transport: NetworkTransport.Solana,
        nativeDecimals: 9,
        defaultRpcUrls: [
            "https://api.mainnet.solana.com",
            "https://solana-rpc.publicnode.com",
        ],
        explorerUrl: "https://solscan.io",
        minGas: SOLANA_MIN_GAS_TOP_UP_LAMPORTS,
    },
    SONIC: {
        chainName: "Sonic",
        symbol: "SONIC",
        gasToken: "S",
        transport: NetworkTransport.Evm,
        chainId: 146,
        nativeDecimals: 18,
        defaultRpcUrls: [
            "https://rpc.soniclabs.com",
            "https://sonic.api.pocket.network",
        ],
        explorerUrl: "https://sonicscan.org",
    },
    STABLE: {
        chainName: "Stable",
        symbol: "STABLE",
        gasToken: "USDT0",
        transport: NetworkTransport.Evm,
        chainId: 988,
        nativeDecimals: 18,
        defaultRpcUrls: ["https://rpc.stable.xyz"],
        explorerUrl: "https://stablescan.xyz",
    },
    TEMPO: {
        chainName: "Tempo",
        symbol: "USD",
        gasToken: "USD",
        transport: NetworkTransport.Evm,
        chainId: 4217,
        nativeDecimals: 6,
        defaultRpcUrls: ["https://rpc.tempo.xyz"],
        explorerUrl: "https://explore.mainnet.tempo.xyz",
    },
    TRON: {
        chainName: "Tron",
        symbol: "TRX",
        gasToken: "TRX",
        transport: NetworkTransport.Tron,
        nativeDecimals: 6,
        defaultRpcUrls: [
            "https://tron-rpc.publicnode.com",
            "https://api.trongrid.io",
        ],
        explorerUrl: "https://tronscan.org/#",
    },
    UNI: {
        chainName: "Unichain",
        symbol: "UNI",
        gasToken: "ETH",
        transport: NetworkTransport.Evm,
        chainId: 130,
        nativeDecimals: 18,
        defaultRpcUrls: [
            "https://mainnet.unichain.org",
            "https://unichain-rpc.publicnode.com",
        ],
        explorerUrl: "https://uniscan.xyz",
    },
    WORLD: {
        chainName: "World Chain",
        symbol: "WORLD",
        gasToken: "ETH",
        transport: NetworkTransport.Evm,
        chainId: 480,
        nativeDecimals: 18,
        defaultRpcUrls: [
            "https://worldchain-mainnet.g.alchemy.com/public",
            "https://480.rpc.thirdweb.com",
        ],
        explorerUrl: "https://worldscan.org",
    },
    XDC: {
        chainName: "XDC",
        symbol: "XDC",
        gasToken: "XDC",
        transport: NetworkTransport.Evm,
        chainId: 50,
        nativeDecimals: 18,
        defaultRpcUrls: ["https://rpc.xdc.org", "https://rpc.ankr.com/xdc"],
        explorerUrl: "https://xdcscan.com",
    },
    XLAYER: {
        chainName: "XLayer",
        symbol: "XLAYER",
        gasToken: "OKB",
        transport: NetworkTransport.Evm,
        chainId: 196,
        nativeDecimals: 18,
        defaultRpcUrls: ["https://xlayerrpc.okx.com"],
        explorerUrl: "https://www.oklink.com/x-layer",
    },
} as const satisfies Record<string, ChainMeta>;

export type ChainKey = keyof typeof chains;
